import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dahbb33p14a1h3mr6hk0";

type SubmitStatus = "idle" | "loading" | "success" | "error";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function SubscribeSection() {
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [feedback, setFeedback] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;

    const rawData = new FormData(form);
    const honeypot = String(rawData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("订阅成功，之后有首发变化会推送给你。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    rawData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() === "") return;
      payload.append(key, value);
    });

    setStatus("loading");
    setFeedback("正在提交…");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("订阅成功，之后有首发变化会推送给你。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(serverMsg.trim() || "提交没有成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，提交没有成功，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8">
        <div className="relative overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20deep%20crimson%20and%20dark%20green%20painterly%20ripple%20texture%20on%20ink%20black%20paper%2C%20soft%20directional%20light%2C%20wide%20panoramic%20editorial%20banner%20with%20dark%20negative%20space%20on%20the%20left%2C%20no%20people&width=1600&height=700&seq=gua-cta-bg-01&orientation=landscape"
            alt="订阅首发推送的抽象背景"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-primary-950/75 to-foreground-950/85" />

          <div className="relative z-10 grid grid-cols-1 items-center gap-8 p-6 md:p-10 lg:grid-cols-2 lg:gap-12 lg:p-12">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 text-xs text-background-50 backdrop-blur">
                <i className="ri-notification-3-line" aria-hidden />
                首发推送
              </span>
              <h2 className="mt-4 font-heading text-2xl font-black leading-tight text-background-50 md:text-3xl">
                有变化才提醒你，不刷屏
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-background-100/80">
                只推送状态发生变化的条目：出现回应、出现反转、或者补充了新的时间点。
                其余时间保持安静。
              </p>
            </div>

            <div className="rounded-lg bg-background-50 p-5 md:p-6">
              <form
                data-readdy-form
                id="subscribe-form"
                onSubmit={handleSubmit}
                className="flex flex-col gap-4"
              >
                <div>
                  <label
                    htmlFor="subscribe-email"
                    className="block text-sm font-medium text-foreground-900"
                  >
                    邮箱地址
                  </label>
                  <input
                    id="subscribe-email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@example.com"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label
                    htmlFor="subscribe-focus"
                    className="block text-sm font-medium text-foreground-900"
                  >
                    想优先关注的方向
                  </label>
                  <select
                    id="subscribe-focus"
                    name="focus"
                    defaultValue="影视剧组"
                    className="mt-1.5 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                  >
                    <option value="影视剧组">影视剧组</option>
                    <option value="音乐现场">音乐现场</option>
                    <option value="综艺录制">综艺录制</option>
                    <option value="电竞圈">电竞圈</option>
                    <option value="直播圈">直播圈</option>
                    <option value="选秀舞台">选秀舞台</option>
                  </select>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-guard"
                />

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i
                    className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
                    aria-hidden
                  />
                  {status === "loading" ? "提交中…" : "订阅首发推送"}
                </button>

                {status !== "idle" && feedback && (
                  <p
                    role="status"
                    className={`rounded-md px-3 py-2.5 text-sm ${
                      status === "error"
                        ? "bg-primary-50 text-primary-800"
                        : "bg-accent-50 text-accent-900"
                    }`}
                  >
                    {feedback}
                  </p>
                )}

                <p className="text-xs leading-relaxed text-foreground-500">
                  提交后仅用于发送本条推送提醒，随时可以回信退订。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}